import { useEffect, useState } from 'react';
import useUserDetails from '../../hooks/useUserDetails';
import { resolveImageTag } from '../../../services/visualizationService';

interface VizImageProps {
  src: string;
  alt?: string;
}

interface TaggedVizImageProps {
  tag: string;
  alt?: string;
}

const VizImage = ({ src, alt }: VizImageProps) => (
  <span className="flex flex-col gap-1">
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img
        src={src}
        alt={alt ?? ''}
        className="max-h-[420px] w-full rounded-md border border-[#e5e5e5] object-contain"
      />
    </a>
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="text-[12px] text-[#525252] underline underline-offset-2 hover:text-[#171717]"
    >
      {alt ? `Open “${alt}” full size` : 'Open full size'} ↗
    </a>
  </span>
);

// Presigned URLs returned by the backend expire (~30 min), so chat stores stable
// `xqviz://` / `xqsurviz://` tags instead. We resolve each tag to a fresh URL at render time and cache
// it briefly, with a TTL well under the presigned lifetime so remounts never reuse an
// expired URL.
const RESOLVED_TAG_TTL_MS = 10 * 60 * 1000;
const resolvedTagCache = new Map<string, { url: string; resolvedAt: number }>();

const getCachedUrl = (tag: string): string | null => {
  const entry = resolvedTagCache.get(tag);
  if (!entry) return null;
  if (Date.now() - entry.resolvedAt > RESOLVED_TAG_TTL_MS) {
    resolvedTagCache.delete(tag);
    return null;
  }
  return entry.url;
};

const TaggedVizImage = ({ tag, alt }: TaggedVizImageProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [url, setUrl] = useState<string | null>(() => getCachedUrl(tag));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (url) return;
    const accessToken = userDetails?.access_token;
    if (!accessToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const resolved = await resolveImageTag(tag, accessToken);
        if (cancelled) return;
        if (!resolved) {
          setFailed(true);
          return;
        }
        resolvedTagCache.set(tag, { url: resolved, resolvedAt: Date.now() });
        setUrl(resolved);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tag, url, userDetails?.access_token]);

  if (failed) {
    return (
      <span className="text-[12px] text-[#a3a3a3] italic">
        {alt ? `Could not load “${alt}”.` : 'Could not load visualization.'}
      </span>
    );
  }
  if (!url) {
    return (
      <span className="flex h-[200px] w-full animate-pulse items-center justify-center rounded-md border border-[#e5e5e5] bg-[#f5f5f5] text-[12px] text-[#a3a3a3]">
        {alt ? `Loading “${alt}”…` : 'Loading visualization…'}
      </span>
    );
  }
  return <VizImage src={url} alt={alt} />;
};

export default TaggedVizImage;
export { VizImage };
